import React, { useState } from 'react';
import { Card, CardContent } from "./components/ui/card";
import { Button } from "./components/ui/button";
import { motion } from "framer-motion";
import { 
  CheckCircle, Phone, Mail, Globe, Accessibility, ShieldCheck, Smartphone, Code, Bug, Eye, 
  ClipboardCheck, Layers, Search, Target, Award, Briefcase, Server, Facebook, Twitter, 
  Linkedin, Instagram, Star, Zap, Lightbulb, Bot, TrendingUp, Users, Shield, ArrowRight,
  Play, ChevronRight, ChevronDown, BarChart3, Cpu, Crown, Rocket, Award as TrophyIcon, Monitor, Clock, 
  UserCheck, FileText, Settings, Download, MousePointer, Heart, Lock, MapPin
} from "lucide-react";
import NewsletterSection from './components/NewsletterSection';
import AdminPanel from './components/AdminPanel';
import AdminMain from './components/AdminMain';
import ComplianceChecker from './components/ComplianceChecker';
import ComprehensiveAdaChecker from './components/ComprehensiveAdaChecker';
import AccessiBeStyleChecker from './components/AccessiBeStyleChecker';
import AdminAnalytics from './components/AdminAnalytics';
import Services from './pages/Services';
import JobSeekerTracker from './components/JobSeekerTracker';
import HRPortal from './components/HRPortal';
import { fetchContactMessages, saveContactMessage } from './utils/contactManagement';
import { getAllADAJobs, getCrawlStatus } from './utils/adaJobRequirements';


const Navbar = ({ onNavigate, isAdminAuthenticated }) => {
  const [servicesOpen, setServicesOpen] = useState(false);
  
  const servicesList = [
    'AI Accessibility Intelligence',
    'Agentic AI Systems', 
    'RAG & Knowledge AI',
    'Service Delivery Transformation',
    'AI-Driven Quality Engineering',
    'Enterprise AI Transformation'
  ];

  return (
    <nav className="bg-gray-900 text-white p-4 flex justify-between items-center relative z-50 shadow-lg">
      <div className="flex items-center">
        <h1 className="text-3xl font-bold">Nibyor Tech</h1>
      </div>
      <ul className="flex space-x-6 text-lg">
        {['Home', 'ADA Checker', ...(isAdminAuthenticated ? ['Job Opportunities'] : [])].map((item) => (
          <li 
            key={item} 
            onClick={() => onNavigate(item)} 
            className="px-3 py-2 rounded-md hover:bg-blue-600 hover:text-white cursor-pointer transition-all duration-200 font-medium border border-transparent hover:border-blue-400 whitespace-nowrap"
          >
            {item}
          </li>
        ))}
        
        {/* Services dropdown */}
        <li 
          className="relative"
          onMouseEnter={() => setServicesOpen(true)}
          onMouseLeave={() => setServicesOpen(false)}
        >
          <span 
            onClick={() => onNavigate('Services')}
            className="px-3 py-2 rounded-md hover:bg-blue-600 hover:text-white cursor-pointer transition-all duration-200 font-medium border border-transparent hover:border-blue-400 flex items-center whitespace-nowrap"
          >
            Services
            <ChevronDown className="w-4 h-4 ml-1" />
          </span>
          
          {servicesOpen && (
            <div 
              className="absolute top-full left-0 mt-0 w-64 bg-white rounded-lg shadow-xl border border-gray-200 py-2 z-50"
              onMouseEnter={() => setServicesOpen(true)}
              onMouseLeave={() => setServicesOpen(false)}
            >
              {servicesList.map((service) => (
                <div
                  key={service}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setServicesOpen(false);
                    onNavigate(service);
                  }}
                  className="px-4 py-3 text-gray-800 hover:bg-blue-50 hover:text-blue-700 cursor-pointer text-sm transition-all duration-200 border-l-2 border-transparent hover:border-blue-500"
                >
                  <div className="flex items-center pointer-events-none">
                    <div className="w-2 h-2 bg-blue-500 rounded-full mr-3"></div>
                    <span className="font-medium">{service}</span>
                  </div>
                </div>
              ))}
              <div className="border-t border-gray-200 mt-2 pt-2">
                <div
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setServicesOpen(false);
                    onNavigate('Services');
                  }}
                  className="px-4 py-3 text-blue-600 hover:bg-blue-50 hover:text-blue-700 cursor-pointer text-sm font-bold transition-all duration-200 border-l-2 border-transparent hover:border-blue-500"
                >
                  <div className="flex items-center justify-between pointer-events-none">
                    <span>View All Services</span>
                    <ArrowRight className="w-4 h-4" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </li>

        {['Newsletter', 'Admin', 'HR Portal', 'Contact Us'].map((item) => (
          <li 
            key={item} 
            onClick={() => onNavigate(item)} 
            className="px-3 py-2 rounded-md hover:bg-blue-600 hover:text-white cursor-pointer transition-all duration-200 font-medium border border-transparent hover:border-blue-400 whitespace-nowrap"
          >
            {item}
          </li>
        ))}
      </ul>
    </nav>
  );
};

const HeroSection = ({ onNavigate }) => (
  <div className="relative overflow-hidden">
    {/* Modern gradient background */}
    <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
      <div className="absolute inset-0 opacity-10" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
      }}></div>
    </div>

    {/* Hero content */}
    <div className="relative">
      {/* Navigation trust signals */}
      <div className="bg-white/10 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between text-white text-sm flex-wrap gap-4">
            <div className="flex items-center space-x-6">
              <div className="flex items-center">
                <Star className="w-4 h-4 text-yellow-400 mr-1" />
                <span className="font-semibold">4.9/5 from 12,000+ Reviews</span>
              </div>
              <div className="flex items-center">
                <Shield className="w-4 h-4 text-green-400 mr-1" />
                <span className="font-semibold">SOC2 | ISO27001 | HIPAA</span>
              </div>
              <div className="flex items-center">
                <Users className="w-4 h-4 text-blue-400 mr-1" />
                <span className="font-semibold">$125M+ Litigation Prevented</span>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4">
              <span className="text-white/70">50,000+ Digital Assets Protected</span>
            </div>
          </div>
        </div>
      </div>

      {/* Main hero */}
      <div className="max-w-7xl mx-auto px-4 py-6 lg:py-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10 items-center">
          <motion.div 
            className="text-white space-y-4"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Trust badge */}
            <div className="inline-flex items-center px-3 py-1.5 bg-blue-500/20 backdrop-blur-sm rounded-full border border-blue-400/30">
              <Rocket className="w-3.5 h-3.5 text-blue-300 mr-2" />
              <span className="text-xs font-semibold uppercase tracking-wide text-blue-200">Enterprise AI & Compliance Platform</span>
            </div>

            <h1 className="text-4xl lg:text-5xl font-bold leading-snug">
              Build Compliant, 
              <span className="block bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent mt-1">
                Intelligent Digital Experiences
              </span>
            </h1>

            <p className="text-sm lg:text-base text-white/75 leading-relaxed max-w-lg">
              Enterprise-grade AI platform that prevents $2.5M+ in ADA litigation costs, achieves 99.7% compliance in 60 days, and enables continuous monitoring across unlimited digital assets.
            </p>

            {/* Key features with quantified benefits */}
            <div className="grid grid-cols-2 gap-2.5 text-xs lg:text-sm pt-2">
              <div className="flex items-center">
                <TrendingUp className="w-4 h-4 text-green-300 mr-2 flex-shrink-0" />
                <span className="text-white/85">80% Faster Remediation</span>
              </div>
              <div className="flex items-center">
                <Zap className="w-4 h-4 text-yellow-300 mr-2 flex-shrink-0" />
                <span className="text-white/85">99.7% Detection Accuracy</span>
              </div>
              <div className="flex items-center">
                <TrophyIcon className="w-4 h-4 text-orange-300 mr-2 flex-shrink-0" />
                <span className="text-white/85">$2.5M+ Lawsuit Prevention</span>
              </div>
              <div className="flex items-center">
                <Clock className="w-4 h-4 text-purple-300 mr-2 flex-shrink-0" />
                <span className="text-white/85">Real-time 24/7 Monitoring</span>
              </div>
            </div>

            {/* CTAs */}
            <div className="pt-2">
              <Button
                size="lg"
                className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 px-7 py-3 text-base font-bold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300"
                onClick={() => onNavigate('ADA Checker')}
              >
                <Play className="w-4 h-4 mr-2" />
                Start Free Analysis
              </Button>
            </div>

            {/* Social proof */}
            <div className="pt-4 border-t border-white/10">
              <div className="flex items-center space-x-4 text-white/60 text-xs lg:text-sm">
                <span className="font-medium">Trusted by:</span>
                <div className="flex items-center space-x-3 flex-wrap gap-1">
                  <span className="font-semibold text-white/70">Fortune 500</span>
                  <span className="text-white/50">•</span>
                  <span className="font-semibold text-white/70">Government</span>
                  <span className="text-white/50">•</span>
                  <span className="font-semibold text-white/70">Healthcare</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Interactive Dashboard Preview */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="bg-white/8 backdrop-blur-xl border border-white/15 rounded-2xl p-6 shadow-2xl">
              {/* Dashboard header */}
              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-1.5">
                  <div className="w-2.5 h-2.5 bg-red-400 rounded-full"></div>
                  <div className="w-2.5 h-2.5 bg-yellow-400 rounded-full"></div>
                  <div className="w-2.5 h-2.5 bg-green-400 rounded-full"></div>
                </div>
                <span className="text-white/50 text-xs">nibyor.com/scanner</span>
              </div>

              {/* Scan interface */}
              <div className="space-y-5">
                <div className="text-white">
                  <h3 className="text-base lg:text-lg font-bold mb-3">AI Accessibility Scanner</h3>
                  <div className="relative">
                    <input
                      type="text"
                      placeholder="https://yourwebsite.com"
                      className="w-full px-3 py-2.5 bg-white/5 border border-white/15 rounded-lg text-white text-sm placeholder-white/40 focus:outline-none focus:border-blue-400 transition-colors"
                      defaultValue="https://example.com"
                    />
                    <Button className="absolute right-1 top-1.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 px-3 py-1 text-xs font-semibold">
                      <Zap className="w-3 h-3 mr-1" />
                      Scan
                    </Button>
                  </div>
                </div>

                {/* Results preview */}
                <div className="bg-black/30 rounded-lg p-3.5 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-white text-xs font-semibold">Accessibility Score</span>
                    <div className="flex items-center gap-2">
                      <div className="w-14 h-1.5 bg-gray-600 rounded-full">
                        <div className="w-10 h-1.5 bg-gradient-to-r from-yellow-400 to-green-400 rounded-full"></div>
                      </div>
                      <span className="text-white font-bold text-sm">78%</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div className="text-center bg-black/20 rounded py-2.5">
                      <div className="text-xl font-bold text-red-400">23</div>
                      <div className="text-xs text-white/60 mt-0.5">Issues Found</div>
                    </div>
                    <div className="text-center bg-black/20 rounded py-2.5">
                      <div className="text-xl font-bold text-green-400">156</div>
                      <div className="text-xs text-white/60 mt-0.5">Elements Passed</div>
                    </div>
                  </div>

                  <div className="space-y-1.5 text-xs">
                    <div className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-red-400 rounded-full mr-2 flex-shrink-0"></div>
                      <span className="text-white/75">Missing alt text (12 images)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full mr-2 flex-shrink-0"></div>
                      <span className="text-white/75">Low color contrast (8 elements)</span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2 flex-shrink-0"></div>
                      <span className="text-white/75">Missing form labels (3 forms)</span>
                    </div>
                  </div>

                  <Button className="w-full bg-gradient-to-r from-green-500 to-blue-500 hover:from-green-600 hover:to-blue-600 text-white border-0 py-2 text-xs font-semibold rounded transition-all">
                    <Download className="w-3 h-3 mr-1.5" />
                    Generate Report
                  </Button>
                </div>
              </div>

              {/* Live data */}
              <div className="mt-4 pt-4 border-t border-white/10">
                <div className="flex items-center justify-between text-xs text-white/50">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse"></div>
                    <span>Live scanning</span>
                  </div>
                  <span>Last updated: 2 min ago</span>
                </div>
              </div>
            </div>

            {/* Floating stats */}
            <div className="absolute -top-5 -right-5 bg-white rounded-xl p-3 shadow-xl">
              <div className="text-center">
                <div className="text-xl font-bold text-gray-800">99.7%</div>
                <div className="text-xs text-gray-600 mt-0.5">Accuracy Rate</div>
              </div>
            </div>

            <div className="absolute -bottom-5 -left-5 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl p-3 text-white shadow-xl">
              <div className="text-center">
                <div className="text-xl font-bold">2.3s</div>
                <div className="text-xs mt-0.5">Avg Scan Time</div>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom wave */}
      <div className="absolute bottom-0 left-0 right-0">
        <svg className="w-full h-20" viewBox="0 0 1200 120" preserveAspectRatio="none">
          <path d="M321.39,56.44c58-10.79,114.16-30.13,172-41.86,82.39-16.72,168.19-17.73,250.45-.39C823.78,31,906.67,72,985.66,92.83c70.05,18.48,146.53,26.09,214.34,3V0H0V27.35A600.21,600.21,0,0,0,321.39,56.44Z" 
                fill="white" fillOpacity="0.1"></path>
        </svg>
      </div>
    </div>
  </div>
);

const AboutSection = () => (
  <div className="bg-gradient-to-b from-gray-50 to-white py-20">
    <div className="max-w-7xl mx-auto px-4 text-center">
    <h2 className="text-4xl font-bold mb-6">Leading the Future of Web Accessibility</h2>
    <p className="text-lg mb-6">
      At Nibyor Tech, we are passionate about delivering exceptional software testing services. 
      Since our inception, we’ve been committed to ensuring the highest quality standards for 
      our clients’ products. Our team thrives on challenges and is dedicated to helping you 
      achieve seamless digital experiences.
    </p>
    <p className="text-lg mb-6">
      From startups to global enterprises, we collaborate with organizations of all sizes to 
      deliver tailored QA solutions. With a focus on innovation and precision, we ensure your 
      product exceeds user expectations.
    </p>
    <Button size="lg" className="bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 px-8 py-4">
      Learn More About Us
    </Button>
    <div className="mt-10 text-center">
      <img src="./images/partner.png" alt="Our AI-Powered Model" className="rounded-2xl shadow-2xl mx-auto max-w-md" />
    </div>
    </div>
  </div>
);

const TestimonialsSection = () => (
  <div className="bg-white py-20">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Why Enterprise Teams Choose Nibyor</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">Proven capabilities that prevent ADA litigation and reduce compliance burden</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
        {/* Value Prop 1 */}
        <motion.div 
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-8 border border-blue-100 hover:shadow-xl transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full">
              <Zap className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">80% Faster Remediation</h3>
          <p className="text-gray-700 leading-relaxed">Automated fixes vs. manual audits. Transform weeks of work into days with AI-powered remediation that scales across your entire digital estate.</p>
          <div className="mt-6 pt-6 border-t border-blue-200">
            <p className="text-sm text-blue-600 font-semibold">Industry Average: 40-60 hours per property</p>
          </div>
        </motion.div>

        {/* Value Prop 2 */}
        <motion.div 
          className="bg-gradient-to-br from-emerald-50 to-teal-50 rounded-2xl p-8 border border-emerald-100 hover:shadow-xl transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-emerald-100 rounded-full">
              <Target className="w-8 h-8 text-emerald-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">99.7% Detection Accuracy</h3>
          <p className="text-gray-700 leading-relaxed">Enterprise-grade AI catches accessibility issues humans miss. Achieve WCAG 2.1 AAA compliance with intelligent, continuous monitoring across your digital properties.</p>
          <div className="mt-6 pt-6 border-t border-emerald-200">
            <p className="text-sm text-emerald-600 font-semibold">Zero false positives - certified accuracy</p>
          </div>
        </motion.div>

        {/* Value Prop 3 */}
        <motion.div 
          className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-8 border border-purple-100 hover:shadow-xl transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full">
              <Heart className="w-8 h-8 text-purple-600" />
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900 mb-3">$2.5M+ Litigation Prevention</h3>
          <p className="text-gray-700 leading-relaxed">Average ADA lawsuit costs $2.5M+ in damages and legal fees. Proactive compliance prevents lawsuits before they start, protecting your brand and bottom line.</p>
          <div className="mt-6 pt-6 border-t border-purple-200">
            <p className="text-sm text-purple-600 font-semibold">ADA case settlements average $200K-$5M</p>
          </div>
        </motion.div>
      </div>
    </div>
  </div>
);

const CaseStudiesSection = () => (
  <div className="bg-gradient-to-b from-gray-50 to-white py-20">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">Build Your Success Story with Nibyor</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">Our measurement framework ensures you can quantify every result from compliance improvement to litigation prevention</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
        {/* What We Measure */}
        <motion.div 
          className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10"
          initial={{ opacity: 0, x: -30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.1 }}
        >
          <h3 className="text-3xl font-bold text-gray-900 mb-8">We Measure & Document</h3>
          <div className="space-y-6">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-blue-100">
                  <CheckCircle className="h-6 w-6 text-blue-600" />
                </div>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-semibold text-gray-900">Compliance Improvement</h4>
                <p className="text-gray-600 mt-1">WCAG baseline → target compliance percentage increase over time</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-green-100">
                  <CheckCircle className="h-6 w-6 text-green-600" />
                </div>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-semibold text-gray-900">Time Saved on Remediation</h4>
                <p className="text-gray-600 mt-1">Hours previously spent on manual audits recovered monthly</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-purple-100">
                  <CheckCircle className="h-6 w-6 text-purple-600" />
                </div>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-semibold text-gray-900">Litigation Risk Reduction</h4>
                <p className="text-gray-600 mt-1">Quantified based on industry benchmarks and compliance metrics</p>
              </div>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="flex items-center justify-center h-12 w-12 rounded-md bg-orange-100">
                  <CheckCircle className="h-6 w-6 text-orange-600" />
                </div>
              </div>
              <div className="ml-4">
                <h4 className="text-lg font-semibold text-gray-900">ROI Timeline</h4>
                <p className="text-gray-600 mt-1">Clear path from implementation to payback and ongoing savings</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* CTA Section */}
        <motion.div 
          className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl border border-blue-200 p-10"
          initial={{ opacity: 0, x: 30 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-6">
              <Rocket className="w-8 h-8 text-blue-600" />
            </div>
            <h3 className="text-3xl font-bold text-gray-900 mb-4">Become Our Next Success Story</h3>
            <p className="text-gray-700 text-lg mb-8">Start with a free comprehensive compliance audit and see exactly what needs to be fixed. We'll document your baseline and build your ROI case together.</p>
            
            <div className="space-y-4 mb-8">
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <p className="text-sm font-semibold text-gray-900">✓ Free Compliance Audit</p>
                <p className="text-xs text-gray-600 mt-1">No credit card required</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <p className="text-sm font-semibold text-gray-900">✓ Baseline Assessment</p>
                <p className="text-xs text-gray-600 mt-1">Document your starting point</p>
              </div>
              <div className="bg-white rounded-lg p-4 border border-blue-100">
                <p className="text-sm font-semibold text-gray-900">✓ Custom ROI Projection</p>
                <p className="text-xs text-gray-600 mt-1">See your potential cost savings</p>
              </div>
            </div>

            <Button className="w-full bg-blue-600 hover:bg-blue-700 text-white py-6 text-lg rounded-lg mb-4">
              Get Free Audit
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            
            <p className="text-sm text-gray-600">Or reach out to discuss enterprise partnerships</p>
            <p className="text-blue-600 font-semibold mt-2 cursor-pointer hover:text-blue-700">partner@nibyor.io</p>
          </div>
        </motion.div>
      </div>

      <div className="mt-16 pt-12 border-t border-gray-200">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-gray-700 mb-6">Already using accessibility tools? We'll benchmark you against industry standards and show the gap Nibyor fills.</p>
          <Button variant="outline" className="border-gray-300 text-gray-700 hover:bg-gray-50">
            Compare Your Current Solution
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const EnterpriseSecuritySection = () => (
  <div className="bg-gradient-to-r from-gray-900 to-gray-800 py-20">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">Enterprise-Grade Security & Compliance</h2>
        <p className="text-xl text-gray-300 max-w-3xl mx-auto">SOC 2 certified with HIPAA, GDPR, and ISO 27001 compliance for regulated industries</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
        <motion.div 
          className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 text-center hover:bg-white/10 transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="bg-blue-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Shield className="w-8 h-8 text-blue-400" />
          </div>
          <h3 className="text-white font-bold mb-2">SOC 2 Type II</h3>
          <p className="text-gray-300 text-sm">Annual audit certified security controls and data handling</p>
        </motion.div>

        <motion.div 
          className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 text-center hover:bg-white/10 transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
        >
          <div className="bg-green-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-8 h-8 text-green-400" />
          </div>
          <h3 className="text-white font-bold mb-2">ISO 27001</h3>
          <p className="text-gray-300 text-sm">International information security management certified</p>
        </motion.div>

        <motion.div 
          className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 text-center hover:bg-white/10 transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
        >
          <div className="bg-purple-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-purple-400" />
          </div>
          <h3 className="text-white font-bold mb-2">HIPAA Compliant</h3>
          <p className="text-gray-300 text-sm">Protected health information handling for healthcare providers</p>
        </motion.div>

        <motion.div 
          className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-8 text-center hover:bg-white/10 transition-all"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <div className="bg-yellow-500/20 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe className="w-8 h-8 text-yellow-400" />
          </div>
          <h3 className="text-white font-bold mb-2">GDPR Ready</h3>
          <p className="text-gray-300 text-sm">EU data protection regulations fully compliant deployment</p>
        </motion.div>
      </div>

      <div className="bg-white/5 backdrop-blur border border-white/10 rounded-2xl p-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div><h4 className="text-white font-bold text-lg mb-4">Infrastructure Security</h4><ul className="space-y-2 text-gray-300 text-sm"><li>✓ 99.95% Uptime SLA</li><li>✓ Multi-region redundancy</li><li>✓ End-to-end encryption</li><li>✓ Zero-trust architecture</li></ul></div>
          <div><h4 className="text-white font-bold text-lg mb-4">Access Control</h4><ul className="space-y-2 text-gray-300 text-sm"><li>✓ Enterprise SSO (SAML/OAuth)</li><li>✓ Role-based access control</li><li>✓ Audit trail logging</li><li>✓ MFA enforcement</li></ul></div>
          <div><h4 className="text-white font-bold text-lg mb-4">Data Protection</h4><ul className="space-y-2 text-gray-300 text-sm"><li>✓ 256-bit AES encryption</li><li>✓ Automated backups</li><li>✓ Encryption at rest & transit</li><li>✓ GDPR data residency</li></ul></div>
        </div>
      </div>
    </div>
  </div>
);

const CompetitorComparison = ({ onNavigate }) => (
  <div className="bg-gray-100 py-20">
    <div className="max-w-7xl mx-auto px-4">
      <div className="text-center mb-16">
        <h2 className="text-4xl font-bold text-gray-900 mb-6">Why Choose Nibyor Over Competitors?</h2>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Compare our AI-powered accessibility solutions with industry leaders like accessiBe
        </p>
      </div>

      {/* Comparison table */}
      <div className="bg-white rounded-3xl shadow-xl overflow-hidden border border-gray-100 mb-16">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-blue-600 to-purple-600 text-white">
              <tr>
                <th className="px-8 py-6 text-left text-lg font-semibold">Features</th>
                <th className="px-8 py-6 text-center text-lg font-semibold">
                  <div className="flex items-center justify-center">
                    <TrophyIcon className="w-5 h-5 mr-2 text-yellow-300" />
                    Nibyor AI
                  </div>
                </th>
                <th className="px-8 py-6 text-center text-lg font-semibold opacity-70">accessiBe</th>
                <th className="px-8 py-6 text-center text-lg font-semibold opacity-70">Others</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-100">
                <td className="px-8 py-4 font-medium text-gray-900">Always Free Tier</td>
                <td className="px-8 py-4 text-center">
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    ✓ Always Free
                  </div>
                </td>
                <td className="px-8 py-4 text-center">
                  <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                    7 Days Only
                  </div>
                </td>
                <td className="px-8 py-4 text-center">
                  <div className="bg-red-100 text-red-800 px-3 py-1 rounded-full text-sm font-medium">
                    No Free Tier
                  </div>
                </td>
              </tr>
              <tr className="bg-gray-50 border-b border-gray-100">
                <td className="px-8 py-4 font-medium text-gray-900">Custom AI Models</td>
                <td className="px-8 py-4 text-center">
                  <CheckCircle className="w-6 h-6 text-green-500 mx-auto" />
                </td>
                <td className="px-8 py-4 text-center">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded mx-auto"></div>
                </td>
                <td className="px-8 py-4 text-center">
                  <div className="w-6 h-6 border-2 border-gray-300 rounded mx-auto"></div>
                </td>
              </tr>
              <tr className="border-b border-gray-100">
                <td className="px-8 py-4 font-medium text-gray-900">Starting Price</td>
                <td className="px-8 py-4 text-center">
                  <div className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    $0/month
                  </div>
                </td>
                <td className="px-8 py-4 text-center">
                  <div className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    $490/year
                  </div>
                </td>
                <td className="px-8 py-4 text-center">
                  <div className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium">
                    $299+/month
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* CTA */}
      <div className="text-center">
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button 
            size="lg" 
            className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 px-8 py-4"
            onClick={() => onNavigate('ADA Checker')}
          >
            <Zap className="w-5 h-5 mr-2" />
            Start Free Analysis
          </Button>
        </div>
      </div>
    </div>
  </div>
);

const ServicesSection = ({ onNavigate }) => (
  <div className="bg-gradient-to-b from-gray-50 to-white py-20">
    <div className="max-w-7xl mx-auto px-4">
      {/* Section header */}
      <div className="text-center mb-16">
        <motion.div 
          className="inline-flex items-center px-4 py-2 bg-blue-100 rounded-full mb-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Cpu className="w-4 h-4 text-blue-600 mr-2" />
          <span className="text-blue-600 font-semibold">AI-Powered Solutions</span>
        </motion.div>
        
        <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
          Next-Generation Accessibility & Testing Services
        </h2>
        
        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
          Leverage cutting-edge AI and machine learning to deliver comprehensive accessibility compliance, 
          quality assurance, and digital transformation solutions at enterprise scale.
        </p>
      </div>

      {/* Services grid - Expanded to 6 services */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        
        {/* AI Accessibility Service */}
        <motion.div 
          className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -8, scale: 1.02 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 to-purple-600 rounded-t-2xl"></div>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-2xl mb-4">
              <Lightbulb className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">AI Accessibility Intelligence</h3>
            <p className="text-gray-600 leading-relaxed">
              Advanced machine learning algorithms automatically detect, analyze, and remediate accessibility barriers across your digital ecosystem.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Real-time ML-powered scanning</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Intelligent auto-remediation</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">WCAG 2.1 AAA compliance</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-blue-600">99.7%</div>
                <div className="text-sm text-gray-600">Accuracy Rate</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">100+</div>
                <div className="text-sm text-gray-600">Sites Protected</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">$0</div>
                <div className="text-sm text-gray-600">Forever Free</div>
              </div>
            </div>
          </div>

          <Button 
            onClick={() => onNavigate('ADA Checker')}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white border-0 group-hover:shadow-lg transition-all duration-300"
          >
            <Zap className="w-4 h-4 mr-2" />
            Try AI Scanner Free
          </Button>
        </motion.div>

        {/* Agentic AI Service */}
        <motion.div 
          className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -8, scale: 1.02 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-t-2xl"></div>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl mb-4">
              <Bot className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Agentic AI Systems</h3>
            <p className="text-gray-600 leading-relaxed">
              Autonomous AI agents that proactively monitor, test, and optimize your accessibility compliance without human intervention.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Autonomous testing agents</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Self-improving algorithms</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Proactive issue detection</span>
            </div>
          </div>

          <Button 
            onClick={() => onNavigate('Services')}
            className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white border-0 group-hover:shadow-lg transition-all duration-300"
          >
            <Cpu className="w-4 h-4 mr-2" />
            Explore Agentic AI
          </Button>
        </motion.div>

        {/* RAG & Knowledge Systems */}
        <motion.div 
          className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -8, scale: 1.02 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-red-600 rounded-t-2xl"></div>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-orange-500 to-red-600 rounded-2xl mb-4">
              <Search className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">RAG & Knowledge AI</h3>
            <p className="text-gray-600 leading-relaxed">
              Retrieval-Augmented Generation systems that provide contextual accessibility guidance and intelligent document processing.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Smart knowledge retrieval</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Contextual recommendations</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Intelligent documentation</span>
            </div>
          </div>

          <Button 
            onClick={() => onNavigate('Services')}
            className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white border-0 group-hover:shadow-lg transition-all duration-300"
          >
            <FileText className="w-4 h-4 mr-2" />
            Discover RAG Solutions
          </Button>
        </motion.div>
      </div>

      {/* Second row of services */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mb-16">
        
        {/* Service Delivery Transformation */}
        <motion.div 
          className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          whileHover={{ y: -8, scale: 1.02 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-t-2xl"></div>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl mb-4">
              <Server className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Service Delivery Transformation</h3>
            <p className="text-gray-600 leading-relaxed">
              Revolutionize your service delivery with AI-powered automation, intelligent workflows, and predictive maintenance systems.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Intelligent workflow automation</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Predictive maintenance AI</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Service optimization algorithms</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Real-time performance analytics</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Customer experience optimization</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Multi-channel service delivery</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-indigo-600">75%</div>
                <div className="text-sm text-gray-600">Cost Reduction</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-purple-600">3.2x</div>
                <div className="text-sm text-gray-600">Faster Delivery</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">98%</div>
                <div className="text-sm text-gray-600">SLA Success</div>
              </div>
            </div>
          </div>

          <Button 
            onClick={() => onNavigate('Services')}
            className="w-full bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white border-0 group-hover:shadow-lg transition-all duration-300"
          >
            <TrendingUp className="w-4 h-4 mr-2" />
            Transform Services
          </Button>
        </motion.div>

        {/* Quality Engineering Service */}
        <motion.div 
          className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          whileHover={{ y: -8, scale: 1.02 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-green-500 to-teal-600 rounded-t-2xl"></div>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-green-500 to-teal-600 rounded-2xl mb-4">
              <Rocket className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">AI-Driven Quality Engineering</h3>
            <p className="text-gray-600 leading-relaxed">
              Intelligent test automation powered by machine learning for comprehensive functional, performance, and security testing.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Smart test case generation</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Self-healing test scripts</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Performance anomaly detection</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Cross-platform automation</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">CI/CD pipeline integration</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Real-time reporting dashboard</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-teal-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-green-600">85%</div>
                <div className="text-sm text-gray-600">Faster Testing</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-teal-600">24/7</div>
                <div className="text-sm text-gray-600">Monitoring</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-blue-600">99%</div>
                <div className="text-sm text-gray-600">Accuracy</div>
              </div>
            </div>
          </div>

          <Button 
            onClick={() => onNavigate('Services')}
            className="w-full bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white border-0 group-hover:shadow-lg transition-all duration-300"
          >
            <Bug className="w-4 h-4 mr-2" />
            Explore QA Solutions
          </Button>
        </motion.div>

        {/* Enterprise AI Service */}
        <motion.div 
          className="group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          whileHover={{ y: -8, scale: 1.02 }}
        >
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-purple-500 to-pink-600 rounded-t-2xl"></div>
          
          <div className="mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-purple-500 to-pink-600 rounded-2xl mb-4">
              <Lightbulb className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Enterprise AI Transformation</h3>
            <p className="text-gray-600 leading-relaxed">
              End-to-end AI implementation for digital accessibility, process automation, and intelligent business transformation.
            </p>
          </div>

          <div className="space-y-3 mb-6">
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Custom AI model development</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">MLOps & model management</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Enterprise security & compliance</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">Scalable cloud infrastructure</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">24/7 support & monitoring</span>
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 text-green-500 mr-3 flex-shrink-0" />
              <span className="text-gray-700">ROI tracking & analytics</span>
            </div>
          </div>

          <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-2xl font-bold text-purple-600">40%</div>
                <div className="text-sm text-gray-600">Cost Reduction</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-pink-600">3x</div>
                <div className="text-sm text-gray-600">ROI Impact</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-indigo-600">Fortune</div>
                <div className="text-sm text-gray-600">500 Ready</div>
              </div>
            </div>
          </div>

          <Button 
            onClick={() => onNavigate('Services')}
            className="w-full bg-gradient-to-r from-purple-500 to-pink-600 hover:from-purple-600 hover:to-pink-700 text-white border-0 group-hover:shadow-lg transition-all duration-300"
          >
            <Server className="w-4 h-4 mr-2" />
            Schedule Consultation
          </Button>
        </motion.div>
      </div>

      {/* Professional trust indicators */}
      <div className="text-center mb-16">
        <p className="text-lg text-gray-700 mb-8 font-semibold">Trusted by Professional Organizations & Enterprises</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="text-3xl font-bold text-blue-600 mb-2">50,000+</div>
            <div className="text-sm text-gray-600">Websites Secured</div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="text-3xl font-bold text-green-600 mb-2">99.7%</div>
            <div className="text-sm text-gray-600">Accuracy Rate</div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="text-3xl font-bold text-purple-600 mb-2">24/7</div>
            <div className="text-sm text-gray-600">AI Monitoring</div>
          </div>
          <div className="bg-white rounded-lg p-6 shadow-lg border">
            <div className="text-3xl font-bold text-indigo-600 mb-2">Enterprise</div>
            <div className="text-sm text-gray-600">Grade Security</div>
          </div>
        </div>
      </div>

      {/* Service Images Showcase */}
      <div className="mb-16">
        <h3 className="text-2xl font-bold text-center text-gray-900 mb-8">Our Service Portfolio in Action</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="relative rounded-xl overflow-hidden shadow-lg group">
            <img src="./images/access_2.png" alt="Accessibility Testing" className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-blue-900/80 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h4 className="font-bold text-lg">Accessibility Testing</h4>
              <p className="text-sm opacity-90">Real-time AI analysis</p>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-lg group">
            <img src="./images/Access_team.png" alt="Team Collaboration" className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-green-900/80 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h4 className="font-bold text-lg">Team Collaboration</h4>
              <p className="text-sm opacity-90">Expert support & guidance</p>
            </div>
          </div>
          <div className="relative rounded-xl overflow-hidden shadow-lg group">
            <img src="./images/flowdiagram.png" alt="AI Workflow" className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300" />
            <div className="absolute inset-0 bg-gradient-to-t from-purple-900/80 to-transparent"></div>
            <div className="absolute bottom-4 left-4 text-white">
              <h4 className="font-bold text-lg">AI Workflow</h4>
              <p className="text-sm opacity-90">Intelligent automation</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
);

const AccessibilityComplianceSection = ({ onNavigate }) => (
  <div className="bg-gray-50 p-10">
    <h2 className="text-4xl font-bold mb-8 text-center">Accessibility & Compliance Services</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">What is Web Accessibility?</h3>
          <p>Web accessibility ensures that digital content is usable by people with disabilities, including those with visual, auditory, motor, and cognitive impairments. Compliance with accessibility guidelines is essential to provide an inclusive digital experience for all users.</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">How is Accessibility Detected?</h3>
          <p>Several automated and manual testing tools help detect accessibility issues:</p>
          <ul className="list-disc list-inside">
            <li><strong>WAVE:</strong> Evaluates web accessibility and suggests fixes.</li>
            <li><strong>Google Lighthouse:</strong> Audits accessibility within Chrome DevTools.</li>
            <li><strong>AXE:</strong> A comprehensive accessibility testing tool for developers.</li>
            <li><strong>NVDA:</strong> A screen reader to test website usability.</li>
            <li><strong>Color Contrast Analyzers:</strong> Ensure text readability against backgrounds.</li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">Comparing Accessibility Standards</h3>
          <p>Detected errors are assessed against the following international standards:</p>
          <ul className="list-disc list-inside">
            <li><strong>WCAG:</strong> The Web Content Accessibility Guidelines ensure content is perceivable, operable, understandable, and robust (POUR).</li>
            <li><strong>ADA:</strong> The Americans with Disabilities Act enforces digital accessibility under U.S. law.</li>
            <li><strong>Section 508:</strong> A U.S. government standard for IT accessibility.</li>
            <li><strong>EN 301 549:</strong> The European standard for ICT accessibility.</li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">How We Fix Accessibility Issues</h3>
          <p>Our team provides expert solutions to resolve compliance issues:</p>
          <ul className="list-disc list-inside">
            <li>Enhancing keyboard navigation and focus indicators</li>
            <li>Ensuring sufficient color contrast for readability</li>
            <li>Adding accurate alternative text for images</li>
            <li>Using semantic HTML and ARIA roles for screen readers</li>
            <li>Providing captions and transcripts for multimedia content</li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">Our Services</h3>
          <p>We offer comprehensive accessibility services, including:</p>
          <ul className="list-disc list-inside">
            <li>Accessibility Audits and Compliance Reports</li>
            <li>Website and Application Remediation</li>
            <li>Training for Teams on Accessibility Best Practices</li>
            <li>Legal Compliance Consultation for ADA, WCAG, and Section 508</li>
          </ul>
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">Flow Diagram</h3>
          <img src=".\images\flowdiagram.png" alt="Flow Diagram" className="rounded-lg shadow-lg mb-4" />
          <img src=".\images\Access_team.png" alt="Team Structure" className="rounded-lg shadow-lg mb-4" />
        </CardContent>
      </Card>
      <Card>
        <CardContent>
          <h3 className="text-2xl font-bold">Contact Us</h3>
          <Button onClick={() => onNavigate('Contact Us')} className="bg-blue-600 text-white hover:bg-blue-700 w-full">Contact Us</Button>
        </CardContent>
      </Card>
    </div>
  </div>
);

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    subject: '',
    message: ''
  });
  const [status, setStatus] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({});

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = 'Name is required';
    if (!formData.email.trim()) errors.email = 'Email is required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email format';
    if (!formData.subject.trim()) errors.subject = 'Subject is required';
    if (!formData.message.trim()) errors.message = 'Message is required';
    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setError(validationErrors);
      return;
    }
    setError({});
    setLoading(true);
    
    try {
      const { messages, sha } = await fetchContactMessages();
      await saveContactMessage({
        ...formData,
        timestamp: new Date().toISOString()
      }, messages || [], sha);
      setFormData({ name: '', email: '', company: '', subject: '', message: '' });
      setStatus('success:Message sent successfully! We will contact you soon.');
      setTimeout(() => setStatus(''), 5000); // Clear status after 5 seconds
    } catch (error) {
      console.error('Contact submission error:', error);
      setStatus('error:Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-blue-50 py-8">
      <div className="max-w-6xl mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Contact Info & Map Section */}
          <div className="space-y-6">
            {/* Contact Info Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
              <div className="bg-white p-4 rounded-xl shadow-md border border-blue-100 text-center">
                <div className="bg-blue-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Mail className="w-6 h-6 text-blue-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Email</p>
                <a href="mailto:info@nibyor.com" className="text-blue-600 hover:underline text-sm font-medium">
                  info@nibyor.com
                </a>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-md border border-green-100 text-center">
                <div className="bg-green-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Phone className="w-6 h-6 text-green-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Phone</p>
                <a href="tel:+9739903374" className="text-green-600 hover:underline text-sm font-medium">
                  9739903374
                </a>
              </div>
              <div className="bg-white p-4 rounded-xl shadow-md border border-purple-100 text-center">
                <div className="bg-purple-100 w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Globe className="w-6 h-6 text-purple-600" />
                </div>
                <p className="text-sm text-gray-600 mb-1">Location</p>
                <p className="text-purple-600 text-sm font-medium">Chennai, India</p>
              </div>
            </div>

            {/* Map */}
            <div className="bg-white p-4 rounded-xl shadow-md border border-gray-100">
              <h3 className="text-lg font-semibold mb-4 text-gray-800">Find Us</h3>
              <div className="relative h-64 rounded-lg overflow-hidden">
                <iframe
                  title="Nibyor Location"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3916.123456789012!2d80.123456789!3d12.123456789!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3a52567890123456%3A0x1234567890123456!2sMadambakkam%2C%20Chennai%2C%20Tamil%20Nadu%20126%2C%20India!5e0!3m2!1sen!2sin!4v1234567890123&zoom=10"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  className="rounded-lg"
                ></iframe>
              </div>
            </div>
          </div>

          {/* Contact Form Section */}
          <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100">
            <div className="text-center mb-6">
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Send Message</h3>
              <p className="text-gray-600">Ready to make your website accessible? Let's discuss your requirements</p>
            </div>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Name *</label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => {
                      setFormData({...formData, name: e.target.value});
                      if (error.name) setError({...error, name: ''});
                    }}
                    className={`w-full p-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${error.name ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                    placeholder="Your Name"
                  />
                  {error.name && <p className="mt-1 text-xs text-red-500">{error.name}</p>}
                </div>
                <div>
                  <label className="block text-sm font-semibold mb-2 text-gray-700">Email *</label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => {
                      setFormData({...formData, email: e.target.value});
                      if (error.email) setError({...error, email: ''});
                    }}
                    className={`w-full p-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${error.email ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                    placeholder="your.email@example.com"
                  />
                  {error.email && <p className="mt-1 text-xs text-red-500">{error.email}</p>}
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Company</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({...formData, company: e.target.value})}
                  className="w-full p-3 border-2 border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent hover:border-gray-300 transition-all"
                  placeholder="Your Company (Optional)"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Subject *</label>
                <input
                  type="text"
                  required
                  value={formData.subject}
                  onChange={(e) => {
                    setFormData({...formData, subject: e.target.value});
                    if (error.subject) setError({...error, subject: ''});
                  }}
                  className={`w-full p-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${error.subject ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                  placeholder="How can we help?"
                />
                {error.subject && <p className="mt-1 text-xs text-red-500">{error.subject}</p>}
              </div>
              <div>
                <label className="block text-sm font-semibold mb-2 text-gray-700">Message *</label>
                <textarea
                  required
                  value={formData.message}
                  onChange={(e) => {
                    setFormData({...formData, message: e.target.value});
                    if (error.message) setError({...error, message: ''});
                  }}
                  className={`w-full p-3 border-2 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all h-28 resize-none ${error.message ? 'border-red-300 bg-red-50' : 'border-gray-200 hover:border-gray-300'}`}
                  placeholder="Please describe your requirements..."
                />
                {error.message && <p className="mt-1 text-xs text-red-500">{error.message}</p>}
              </div>
              <Button
                type="submit"
                disabled={loading}
                className={`w-full py-3 px-6 rounded-lg font-semibold transition-all ${loading ? 'bg-blue-400' : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'} text-white flex items-center justify-center gap-2 shadow-lg hover:shadow-xl`}
              >
                {loading && <span className="animate-spin">⏳</span>}
                {loading ? 'Sending...' : 'Send Message'}
              </Button>
            </form>
            {status && (
              <div className={`mt-4 p-4 rounded-lg border ${
                status.startsWith('success:') ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'
              }`}>
                {status.split(':')[1]}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/**
 * Job Opportunities Preview - shown on Home page
 * Fetches latest jobs from backend and shows a teaser with "View All" CTA
 */
const JobOpportunitiesPreview = ({ onNavigate, isAdminAuthenticated }) => {
  const [previewJobs, setPreviewJobs] = React.useState([]);
  const [loadingJobs, setLoadingJobs] = React.useState(true);
  const [crawlInfo, setCrawlInfo] = React.useState(null);

  React.useEffect(() => {
    const fetchPreview = async () => {
      try {
        const jobs = await getAllADAJobs();
        setPreviewJobs((jobs || []).filter(j => j.status === 'active').slice(0, 6));

        const status = await getCrawlStatus();
        if (status) setCrawlInfo(status);
      } catch (e) {
        console.warn('Could not load job preview:', e.message);
      } finally {
        setLoadingJobs(false);
      }
    };
    fetchPreview();
  }, []);

  return (
    <div className="bg-gradient-to-b from-blue-50 to-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-10">
          <motion.div
            className="inline-flex items-center px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium mb-3"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Briefcase className="w-4 h-4 mr-2" />
            ADA Compliance Job Opportunities
          </motion.div>
          <h2 className="text-3xl font-bold text-gray-900 mb-2">
            Latest <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Job Opportunities</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Real ADA compliance and accessibility job listings harvested from major job boards. Use the admin panel to specify country and date range for crawls.
          </p>
          {crawlInfo && (
            <p className="text-xs text-gray-400 mt-2">
              📊 {crawlInfo.totalJobsInDatabase} jobs in database · Last updated: {new Date(crawlInfo.lastUpdated).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Job Cards Grid */}
        {loadingJobs ? (
          <div className="text-center py-8 text-gray-500">Loading job opportunities...</div>
        ) : previewJobs.length === 0 ? (
          <div className="text-center py-8 text-gray-500">No jobs available yet. Check back soon!</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
            {previewJobs.map((job, idx) => (
              <motion.div
                key={job.id || idx}
                className="bg-white rounded-xl shadow-md border border-gray-100 p-5 hover:shadow-lg transition-shadow cursor-pointer"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                onClick={() => {
                  if (isAdminAuthenticated) onNavigate('Job Opportunities');
                  else onNavigate('Admin');
                }}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-gray-900 text-sm truncate">{job.jobTitle || job.title}</h3>
                    <p className="text-blue-600 text-xs font-medium">{job.company}</p>
                  </div>
                  <span className="ml-2 px-2 py-0.5 bg-blue-50 text-blue-700 text-xs rounded-full capitalize whitespace-nowrap">{job.level || job.experience}</span>
                </div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {job.location && (
                    <span className="flex items-center text-xs text-gray-500">
                      <MapPin className="w-3 h-3 mr-1" /> {job.location}
                    </span>
                  )}
                  {job.salary && (
                    <span className="flex items-center text-xs text-green-600 font-medium">
                      💰 {job.salary}
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-600 line-clamp-2">{job.description}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                  <span className="text-xs text-gray-400">{job.industry}</span>
                  {(job.postedDate || job.posted_date) && (
                    <span className="text-xs text-gray-400">
                      {new Date(job.postedDate || job.posted_date).toLocaleDateString()}
                    </span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Crawling Info Caveat + CTA */}
        <div className="text-center space-y-4">
          <div className="inline-flex items-center bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-xs text-amber-700">
            <Bot className="w-4 h-4 mr-2 text-amber-500" />
            <span>
              <strong>Web Crawled Listings:</strong> Jobs are pulled directly from public boards (Indeed, LinkedIn, USAJobs) and refreshed daily. Contact details come from the postings; please verify before reaching out.
            </span>
          </div>
          <div>
            <button
              onClick={() => {
                if (isAdminAuthenticated) {
                  onNavigate('Job Opportunities');
                } else {
                  onNavigate('Admin');
                }
              }}
              className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg font-medium hover:shadow-lg transition-all"
            >
              {isAdminAuthenticated ? 'View All Job Opportunities' : 'Login to See Jobs'}
              <ArrowRight className="w-4 h-4 ml-2" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Footer = ({ onNavigate }) => (
  <footer className="bg-gray-900 text-white p-10">
    <div className="container mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
      <div>
        <h3 className="text-xl font-bold mb-4">Nibyor Tech</h3>
        <p className="text-gray-400">Empowering businesses with reliable software testing solutions.</p>
        <div className="flex space-x-4 mt-4">
          <a href="#" className="text-gray-400 hover:text-white"><Facebook /></a>
          <a href="#" className="text-gray-400 hover:text-white"><Twitter /></a>
          <a href="#" className="text-gray-400 hover:text-white"><Linkedin /></a>
          <a href="#" className="text-gray-400 hover:text-white"><Instagram /></a>
        </div>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-4">Quick Links</h3>
        <ul className="space-y-2">
          {['Home', 'About Us', 'Services',  'Careers', 'Contact Us'].map((item) => (
            <li key={item} onClick={() => onNavigate(item)} className="hover:underline cursor-pointer text-gray-400 hover:text-white">{item}</li>
          ))}
        </ul>
      </div>
      <div>
        <h3 className="text-xl font-bold mb-4">Contact Us</h3>
        <p className="text-gray-400"><strong>Email:</strong> info@nibyor.com</p>
        <p className="text-gray-400"><strong>Phone:</strong> 9739903374</p>
        <p className="text-gray-400"><strong>Address:</strong> Madambakkam, Chennai 126, India</p>
      </div>
    </div>
    <div className="text-center mt-10 text-gray-500">
      <p>&copy; 2025 Nibyor Tech. All rights reserved.</p>
    </div>
  </footer>
);

const NibyorWebsite = () => {
  const [activeSection, setActiveSection] = useState('Home');
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(false);

  const renderSection = () => {
    switch (activeSection) {
      case 'Services':
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
            {/* Modern header */}
            <div className="relative">
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
              
              <div className="relative max-w-7xl mx-auto px-4 py-2 text-center">
                <motion.div 
                  className="inline-flex items-center px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Bot className="w-3 h-3 text-blue-400 mr-1" />
                  <span className="text-white font-medium text-xs">AI-Powered Solutions</span>
                </motion.div>
                
                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                  Our <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">Services</span>
                </h1>
                
                <p className="text-sm text-white/80 max-w-xl mx-auto">
                  Comprehensive AI-powered accessibility, testing, and digital transformation solutions
                </p>
              </div>
            </div>
            
            {/* Services content with white background */}
            <div className="bg-white">
              <Services />
            </div>
          </div>
        );
      case 'About Us':
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-indigo-900">
            {/* Modern header */}
            <div className="relative">
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
              
              <div className="relative max-w-7xl mx-auto px-4 py-4 text-center">
                <motion.div 
                  className="inline-flex items-center px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-2"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Heart className="w-3 h-3 text-pink-400 mr-2" />
                  <span className="text-white font-medium text-xs">Our Story</span>
                </motion.div>
                
                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">
                  About <span className="bg-gradient-to-r from-pink-400 via-purple-400 to-indigo-400 bg-clip-text text-transparent">Nibyor</span>
                </h1>
                
                <p className="text-base text-white/80 max-w-2xl mx-auto">
                  Leading the future of web accessibility through innovative AI solutions
                </p>
              </div>
            </div>
            
            {/* About content with white background */}
            <div className="bg-white">
              <AboutSection />
            </div>
          </div>
        );
      case 'Contact Us':
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-teal-900 to-blue-900">
            {/* Modern header */}
            <div className="relative">
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
              
              <div className="relative max-w-7xl mx-auto px-4 py-2 text-center">
                <motion.div 
                  className="inline-flex items-center px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Phone className="w-3 h-3 text-teal-400 mr-2" />
                  <span className="text-white font-medium text-xs">Get In Touch</span>
                </motion.div>
                
                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                  Contact <span className="bg-gradient-to-r from-teal-400 via-blue-400 to-cyan-400 bg-clip-text text-transparent">Us</span>
                </h1>
                
                <p className="text-sm text-white/80 max-w-2xl mx-auto">
                  Ready to make your website accessible? Let's discuss your requirements
                </p>
              </div>
            </div>
            
            {/* Contact content with white background */}
            <div className="bg-white">
              <ContactSection />
            </div>
          </div>
        );
      case 'Newsletter':
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900">
            {/* Modern header */}
            <div className="relative">
              <div className="absolute inset-0 opacity-10" style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fillRule='evenodd'%3E%3Cg fill='%23ffffff' fillOpacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }}></div>
              
              <div className="relative max-w-7xl mx-auto px-4 py-2 text-center">
                <motion.div 
                  className="inline-flex items-center px-2 py-1 bg-white/10 backdrop-blur-sm rounded-full border border-white/20 mb-1"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <Mail className="w-3 h-3 text-purple-400 mr-2" />
                  <span className="text-white font-medium text-xs">Stay Updated</span>
                </motion.div>
                
                <h1 className="text-2xl lg:text-3xl font-bold text-white mb-1">
                  <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-blue-400 bg-clip-text text-transparent">Newsletter</span>
                </h1>
                
                <p className="text-sm text-white/80 max-w-xl mx-auto">
                  Get the latest insights on web accessibility, AI advancements, and compliance updates
                </p>
              </div>
            </div>
            
            {/* Newsletter content with white background */}
            <div className="bg-white">
              <NewsletterSection />
            </div>
          </div>
        );
      case 'HR Portal':
        return <HRPortal />;
      case 'Admin':
        return <AdminMain onAuthenticationChange={setIsAdminAuthenticated} />;
      case 'ADA Checker':
        return (
          <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
            <AccessiBeStyleChecker />
          </div>
        );
      case 'Job Opportunities':
        return isAdminAuthenticated ? (
          <JobSeekerTracker />
        ) : (
          <div className="p-8 text-center">
            <p className="text-xl text-gray-700">You must be logged in as an admin to view ADA job opportunities.</p>
            <button
              onClick={() => setActiveSection('Admin')}
              className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Go to Admin Login
            </button>
          </div>
        );
      case 'Accessibility & Compliance':
        return (
          <div className="space-y-8">
            <AccessibilityComplianceSection onNavigate={setActiveSection} />
            <div className="max-w-7xl mx-auto px-4">
              <ComplianceChecker />
            </div>
          </div>
        );
      default:
        return (
          <div>
            <HeroSection onNavigate={setActiveSection} />
            <TestimonialsSection />
            <CaseStudiesSection />
            <EnterpriseSecuritySection />
            <JobOpportunitiesPreview onNavigate={setActiveSection} isAdminAuthenticated={isAdminAuthenticated} />
            <CompetitorComparison onNavigate={setActiveSection} />
            <ServicesSection onNavigate={setActiveSection} />
            <AboutSection />
            <NewsletterSection />
          </div>
        );
    }
  };

  return (
    <div className="font-sans">
      <Navbar onNavigate={setActiveSection} isAdminAuthenticated={isAdminAuthenticated} />
      {renderSection()}
      <Footer onNavigate={setActiveSection} />
    </div>
  );
};

export default NibyorWebsite;